import { Observable, Subject } from 'rxjs';

/** What a panel shows, for placement. Like things open as sibling tabs of
 *  the last group of their kind: a document is any text-based file whatever
 *  the viewer (markdown, Monaco, CSV, preview); the terminal is a full-width
 *  drawer at the bottom; sessions stay together; tools split beside the
 *  active panel. Derived from the component when not given. */
export type PanelKind = 'session' | 'document' | 'terminal' | 'tool' | 'app';

export interface PanelDescriptor {
  id: string;
  component: string;
  title?: string;
  inputs: Record<string, any>;
  outputs: Record<string, any>;
  initialSize?: { rows?: number; cols?: number; x?: number; y?: number };
  position?: PanelPosition;
  kind?: PanelKind;
}

export interface PanelPosition {
  referencePanel?: string;
  referenceGroup?: string;
  direction?: 'left' | 'right' | 'above' | 'below' | 'within';
}

export interface PanelEvent {
  type: 'close' | 'focus' | 'resize' | 'maximize' | 'restore';
  panelId: string;
  data?: any;
}

export interface SerializedLayout {
  type: 'gridster' | 'dockview';
  version: number;
  data: any;
}

export abstract class WindowManager {
  protected _panelEvent$ = new Subject<PanelEvent>();
  public panelEvent$: Observable<PanelEvent> = this._panelEvent$.asObservable();

  abstract init(container: HTMLElement): void;
  abstract dispose(): void;

  abstract addPanel(descriptor: PanelDescriptor): string;
  abstract removePanel(id: string): void;
  abstract hasPanel(id: string): boolean;
  abstract focusPanel(id: string): void;
  /** Show a count beside the panel's title (null clears). Default: no-op. */
  setPanelBadge(_id: string, _count: number | null): void {}

  abstract getLayout(): SerializedLayout;
  abstract restoreLayout(layout: SerializedLayout): void;
  abstract clear(): void;
  abstract layout(): void;
}
